import { useEffect } from "react";

import {
  ENTRY_DATA_CLINICAL_TRIAL_DESIGN,
  ENTRY_DATA_SECTION_KEYS,
} from "@frontend/common";

import { useScroll } from "@FillInTableContainer/components/FillInTable/hooks/useScroll";
import {
  DefaultShellTitle,
  ShellDividerPlusBar,
  TableCreatorTextFieldContainer,
} from "@FillInTableContainer/shared";
import useCheckPressedShell from "@FillInTableContainer/shared/hooks/useCheckPressedShell";
import { useGetInfoOfShell } from "@FillInTableContainer/shared/hooks/useGetInfoOfShell";
import { TableCreatorSubShellList } from "@FillInTableContainer/shared/types/tableCreator.types";
import {
  isAddDeleteShell,
  isRequiredShell,
  getShellPlaceholder,
} from "@FillInTableContainer/shared/utils/getOptionsFromData";
import { getShellTitle } from "@FillInTableContainer/shared/utils/getTextFromData";
import IconXMark from "@images/fillInTable/tableCreator/icon-x-mark-white.svg";
import { useEntryDataValue } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";
import { padStart } from "@utils/padStart/padStart";

import * as S from "./ClinicalTrialDesignShell.style";
import { useHandleClinicalTrialsTableCreatorShell } from "./hooks/useHandleClinicalTrialsTableCreatorShell";

import type { ClinicalTrialsSection } from "@/types/clinicalTrials.types";

export interface PositionInfoProps {
  groupTableKey: ClinicalTrialsSection;
  groupTableIndex: number;
  groupShellKey: string | null;
  groupShellIndex: number;
  subGroupShellIndex: number;
  subGroupShellKey: string | null;
  tableKey: string | null;
  tableIndex: number;
  shellKey: string;
  shellIndex: number;
  shellCount: number;
}

interface Props extends PositionInfoProps {
  shellCount: number;
  subShellList?: TableCreatorSubShellList;
  isShellOverOne: boolean;
  isTitleRowShell?: boolean;
  isFocusedGroupShell: boolean;
  isClearGroupShell: boolean;
  isDeleteGroupShell: boolean;
  isAlertGroupShell: boolean;
  isClearSubGroupShell: boolean;
  isDeleteSubGroupShell: boolean;
}

const ClinicalTrialDesignShell = ({
  groupTableKey,
  groupTableIndex,
  groupShellKey,
  groupShellIndex,
  subGroupShellIndex,
  subGroupShellKey,
  tableKey,
  shellKey,
  tableIndex,
  shellIndex,
  shellCount,
  subShellList,
  isShellOverOne,
  isTitleRowShell = false,
  isClearGroupShell,
  isDeleteGroupShell,
  isAlertGroupShell,
  isClearSubGroupShell,
  isDeleteSubGroupShell,
}: Props) => {
  const positionPropsInfo: PositionInfoProps = {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    subGroupShellIndex,
    subGroupShellKey,
    tableKey,
    tableIndex,
    shellKey,
    shellIndex,
    shellCount,
  };

  const {
    isFocused,
    isFocusedColumn,
    isClearShell,
    isDeleteShell,
    isDeleteColumn,
    isEnableEdit,
    isAlertShell,
    isLastShell,
    focusStatus,
    columnSize,
    isFocusedAddButton,
    isDeleteTable,
    isClearTable,
    handleAddShell,
    handleFocusTextFieldWrapper,
    clickForClearShell,
    handleFocusOutShell,
  } = useHandleClinicalTrialsTableCreatorShell(positionPropsInfo);
  const { isPressed, handleChangePressedStatus } = useCheckPressedShell();

  const { register } = useScroll();

  const articleSection = ENTRY_DATA_SECTION_KEYS.clinicalTrial;
  const entryData = useEntryDataValue(getPressIdFromURL());
  const clinicalTrialDesign1TableLength =
    entryData?.[articleSection]?.[ENTRY_DATA_CLINICAL_TRIAL_DESIGN]?.length ||
    0;
  const drugProfileTableLength =
    entryData?.[articleSection as "Clinical Trials"]?.["Drug Profile"]
      ?.length || 0;

  const { shellValue } = useGetInfoOfShell();

  useEffect(() => {
    const handleCheckClickOutsideShell = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.dataset.button) return;

      if (isFocused) {
        handleFocusOutShell();
      }
    };

    window.addEventListener("click", handleCheckClickOutsideShell);

    return () =>
      window.removeEventListener("click", handleCheckClickOutsideShell);
  }, [handleFocusOutShell, isFocused]);

  return (
    <S.ShellAddWrapper
      isHideComponent={
        (clinicalTrialDesign1TableLength === 1 ||
          drugProfileTableLength === 1) &&
        shellKey === "Trial Intervention"
      }
      {...register(
        `${groupTableKey}__${groupTableIndex}__${tableKey}__${tableIndex}__${groupShellKey}__${groupShellIndex}__${subGroupShellKey}__${subGroupShellIndex}__${shellKey}__${shellIndex}`,
      )}
    >
      {isAddDeleteShell(articleSection, shellKey) && isFocusedAddButton && (
        <ShellDividerPlusBar
          isPositionRight={false}
          onClick={() =>
            handleAddShell({
              tableIndexParameter: tableIndex,
              groupShellIndexParameter: groupShellIndex,
              shellIndexParameter: shellIndex,
              shellKeyParameter: shellKey,
              subGroupShellIndexParameter: subGroupShellIndex,
            })
          }
        />
      )}
      <S.ShellWrapper
        column={columnSize}
        className={`set-min-height-for-information-${
          shellKey === "NCT Name" && clinicalTrialDesign1TableLength === 1
        }`}
      >
        {(isClearShell || isDeleteShell) && isEnableEdit && (
          <S.DeleteTableButtonWrapper
            className={`button-reposition-medium-shell-${
              shellKey === "NCT Name"
            }`}
          >
            <S.DeleteTableButton
              type="button"
              data-button="delete-button"
              onClick={clickForClearShell}
            >
              <img src={IconXMark} alt="button for clear" />
              {focusStatus.type === "clear" ? "Clear" : null}
              {focusStatus.type === "delete" ? "Delete" : null}
            </S.DeleteTableButton>
          </S.DeleteTableButtonWrapper>
        )}
        <S.ShellBorderWrapper
          className={`shell is-shell-title-${!!getShellTitle(
            articleSection,
            shellKey,
          )}`}
          isFocused={
            (isFocused || isFocusedColumn) && focusStatus.type !== "dragging"
          }
          $isDragging={isFocused && focusStatus.type === "dragging"}
          isClearStatus={
            isClearShell ||
            isClearGroupShell ||
            isClearTable ||
            isClearSubGroupShell
          }
          isDeleteStatus={
            isDeleteShell ||
            isDeleteGroupShell ||
            isDeleteTable ||
            isDeleteColumn ||
            isDeleteSubGroupShell
          }
          isAlertStatus={isAlertShell || isAlertGroupShell}
          isDisableEdit={!isEnableEdit}
          isTitleRowShell={isTitleRowShell}
          $isPressed={isPressed}
          column={columnSize}
          onClick={handleFocusTextFieldWrapper}
          onMouseDown={(e) => handleChangePressedStatus(e, true)}
          onMouseUp={(e) => handleChangePressedStatus(e, false)}
        >
          <DefaultShellTitle
            isFocused={isFocused}
            isClearShell={isClearShell}
            isDeleteShell={isDeleteShell}
            isAlertShell={isAlertShell}
            isRequiredShell={isRequiredShell(articleSection, shellKey)}
            shellTitle={getShellTitle(articleSection, shellKey)}
            shellTitleNumbering={
              isShellOverOne ? `${padStart(shellIndex + 1)}` : ""
            }
            columnSize={columnSize}
          />

          {!isTitleRowShell && (
            <>
              {subShellList ? (
                subShellList.map(({ subShellKey }) => (
                  <TableCreatorTextFieldContainer
                    key={`sub-shell-${String(shellIndex)}-${String(
                      subShellKey,
                    )}`}
                    placeholder={
                      getShellPlaceholder(articleSection, subShellKey) ||
                      getShellTitle(articleSection, subShellKey)
                    }
                    shellValue={shellValue(
                      subShellKey,
                      tableIndex,
                      groupShellIndex,
                      shellIndex,
                      subGroupShellIndex,
                    )}
                    shellKey={subShellKey}
                    shellIndex={shellIndex}
                    groupTableKey={groupTableKey}
                    groupTableIndex={groupTableIndex}
                    groupShellKey={groupShellKey}
                    groupShellIndex={groupShellIndex}
                    subGroupShellIndex={subGroupShellIndex}
                    subGroupShellKey={subGroupShellKey}
                    tableKey={tableKey}
                    tableIndex={tableIndex}
                  />
                ))
              ) : (
                <TableCreatorTextFieldContainer
                  placeholder={
                    getShellPlaceholder(articleSection, shellKey) ||
                    getShellTitle(articleSection, shellKey)
                  }
                  shellValue={shellValue(
                    shellKey,
                    tableIndex,
                    groupShellIndex,
                    shellIndex,
                    subGroupShellIndex,
                  )}
                  shellKey={shellKey}
                  shellIndex={shellIndex}
                  groupTableKey={groupTableKey}
                  groupTableIndex={groupTableIndex}
                  groupShellKey={groupShellKey}
                  groupShellIndex={groupShellIndex}
                  subGroupShellIndex={subGroupShellIndex}
                  subGroupShellKey={subGroupShellKey}
                  tableKey={tableKey}
                  tableIndex={tableIndex}
                />
              )}
            </>
          )}
        </S.ShellBorderWrapper>
      </S.ShellWrapper>
      {isAddDeleteShell(articleSection, shellKey) &&
        isLastShell &&
        isFocusedAddButton && (
          <ShellDividerPlusBar
            isPositionRight
            onClick={() =>
              handleAddShell({
                tableIndexParameter: tableIndex,
                groupShellIndexParameter: groupShellIndex,
                shellIndexParameter: shellIndex + 1,
                shellKeyParameter: shellKey,
                subGroupShellIndexParameter: subGroupShellIndex,
              })
            }
          />
        )}
    </S.ShellAddWrapper>
  );
};

export default ClinicalTrialDesignShell;
